import React from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

const LaTeXRenderer = ({ text, inline = false }) => {
  if (!text) return null;

  // Math environments that should be rendered as block math by KaTeX
  const MATH_ENVS = [
    'align', 'align\\*', 'aligned', 'alignat', 'alignat\\*',
    'equation', 'equation\\*',
    'gather', 'gather\\*', 'gathered',
    'multline', 'multline\\*',
    'flalign', 'flalign\\*',
    'cases', 'dcases',
    'pmatrix', 'bmatrix', 'Bmatrix', 'vmatrix', 'Vmatrix', 'matrix',
    'array',
  ].join('|');
  const MATH_ENV_REGEX = new RegExp(
    `\\\\begin\\{(${MATH_ENVS})\\}([\\s\\S]*?)\\\\end\\{\\1\\}`,
    'g'
  );

  const processText = (inputText) => {
    let processedText = inputText;

    // Handle enumerate environments - create a special marker for React processing
    processedText = processedText.replace(
      /\\begin\{enumerate\}([\s\S]*?)\\end\{enumerate\}/g,
      (match, content) => {
        // Split by \item but be more careful about whitespace
        const items = content.split(/\\item\s*/).filter(item => item.trim() !== '');
        // Use a special marker that will be processed later in React
        const listId = Math.random().toString(36).substr(2, 9);
        return `__ENUMERATE_${listId}__${JSON.stringify(items)}__END_ENUMERATE__`;
      }
    );
    
    // Handle itemize environments similarly
    processedText = processedText.replace(
      /\\begin\{itemize\}([\s\S]*?)\\end\{itemize\}/g,
      (match, content) => {
        const items = content.split(/\\item\s*/).filter(item => item.trim() !== '');
        const listId = Math.random().toString(36).substr(2, 9);
        return `__ITEMIZE_${listId}__${JSON.stringify(items)}__END_ITEMIZE__`;
      }
    );

    return processedText;
  };

  const renderMath = (text) => {
    const parts = [];
    let currentIndex = 0;
    
    // Single-pass regex — $$ and \[ come BEFORE \begin{} so nested envs inside $$ are not split out.
    // Note: we can't embed MATH_ENV_REGEX directly (it has a backreference \1 that breaks when
    // wrapped in an outer group). Instead we use [^}]+ for the closing env name.
    const combinedRegex = new RegExp(
      '(' +
        '\\$\\$[\\s\\S]*?\\$\\$' +                              // $$...$$
        '|\\\\\\[[\\s\\S]*?\\\\\\]' +                            // \[...\]
        '|\\\\begin\\{[^}]+\\}[\\s\\S]*?\\\\end\\{[^}]+\\}' +  // \begin{env}...\end{env}
        '|__ENUMERATE_[\\w]+__\\[[\\s\\S]*?\\]__END_ENUMERATE__' +
        '|__ITEMIZE_[\\w]+__\\[[\\s\\S]*?\\]__END_ITEMIZE__' +
        '|\\\\\\([\\s\\S]*?\\\\\\)' +                            // \(...\)
        '|\\$[^$]*?\\$' +                                        // $...$
      ')',
      'g'
    );
    let match;
    
    while ((match = combinedRegex.exec(text)) !== null) {
      // Add text before current match
      if (match.index > currentIndex) {
        parts.push({
          type: 'text',
          content: text.slice(currentIndex, match.index)
        });
      }
      
      const content = match[1];

      // Handle standalone math environments \begin{env}...\end{env}
      if (content.match(/^\\begin\{/)) {
        parts.push({ type: 'block-math', content });
      }
      // Handle enumerate lists
      else if (content.match(/^__ENUMERATE_[\w]+__\[[\s\S]*?\]__END_ENUMERATE__$/)) {
        const listMatch = content.match(/^__ENUMERATE_([\w]+)__(\[[\s\S]*?\])__END_ENUMERATE__$/);
        if (listMatch) {
          try {
            const items = JSON.parse(listMatch[2]);
            parts.push({
              type: 'enumerate',
              content: items
            });
          } catch (e) {
            parts.push({
              type: 'text',
              content: '[Enumerate Parse Error]'
            });
          }
        }
      }
      // Handle itemize lists
      else if (content.match(/^__ITEMIZE_[\w]+__\[[\s\S]*?\]__END_ITEMIZE__$/)) {
        const listMatch = content.match(/^__ITEMIZE_([\w]+)__(\[[\s\S]*?\])__END_ITEMIZE__$/);
        if (listMatch) {
          try {
            const items = JSON.parse(listMatch[2]);
            parts.push({
              type: 'itemize',
              content: items
            });
          } catch (e) {
            parts.push({
              type: 'text',
              content: '[Itemize Parse Error]'
            });
          }
        }
      }
      // Handle math expressions
      else if (content.startsWith('$$') && content.endsWith('$$')) {
        parts.push({
          type: 'block-math',
          content: content.slice(2, -2)
        });
      } else if (content.startsWith('\\[') && content.endsWith('\\]')) {
        parts.push({
          type: 'block-math',
          content: content.slice(2, -2)
        });
      } else if (content.startsWith('\\(') && content.endsWith('\\)')) {
        parts.push({
          type: 'inline-math',
          content: content.slice(2, -2)
        });
      } else if (content.startsWith('$') && content.endsWith('$')) {
        parts.push({
          type: 'inline-math',
          content: content.slice(1, -1)
        });
      }
      
      currentIndex = match.index + match[1].length;
    }
    
    // Add remaining text
    if (currentIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.slice(currentIndex)
      });
    }
    
    return parts.map((part, index) => {
      if (part.type === 'inline-math') {
        try {
          return <InlineMath key={index} math={part.content} />;
        } catch (error) {
          return <span key={index} style={{ color: 'red' }}>[Math Error: {part.content}]</span>;
        }
      } else if (part.type === 'block-math') {
        try {
          return <BlockMath key={index} math={part.content} />;
        } catch (error) {
          return <span key={index} style={{ color: 'red' }}>[Math Error: {part.content}]</span>;
        }
      } else if (part.type === 'enumerate') {
        return (
          <ol key={index} style={{ 
            listStyleType: 'decimal',
            listStylePosition: 'outside',
            paddingLeft: '25px',
            margin: '10px 0'
          }}>
            {part.content.map((item, itemIndex) => (
              <li key={itemIndex} style={{ 
                marginBottom: '8px',
                display: 'list-item'
              }}>
                <LaTeXRenderer text={item.trim()} inline={true} />
              </li>
            ))}
          </ol>
        );
      } else if (part.type === 'itemize') {
        return (
          <ul key={index} style={{ 
            listStyleType: 'disc',
            listStylePosition: 'outside',
            paddingLeft: '25px',
            margin: '10px 0'
          }}>
            {part.content.map((item, itemIndex) => (
              <li key={itemIndex} style={{ 
                marginBottom: '8px',
                display: 'list-item'
              }}>
                <LaTeXRenderer text={item.trim()} inline={true} />
              </li>
            ))}
          </ul>
        );
      } else {
        return <span key={index}>{part.content}</span>;
      }
    });
  };

  const processedText = processText(text);
  const result = renderMath(processedText);

  if (inline) {
    return <span>{result}</span>;
  } else {
    return <div>{result}</div>;
  }
};

export default LaTeXRenderer;
